import React, { useState, useEffect } from "react";
import { Save, X, Image as ImageIcon } from "lucide-react";

const ToolEditForm = ({ tool, onSave, onCancel, categoryDropdown }) => {
    // Helper function to format date to YYYY-MM-DD
    const formatDate = (dateString) => {
        if (!dateString) return new Date().toISOString().split("T")[0];
        const date = new Date(dateString);
        return date.toISOString().split("T")[0];
    };

    // Helper function to format use cases for display
    const formatUseCases = (useCases) => {
        if (!Array.isArray(useCases)) return "";
        return useCases.map((uc) => JSON.stringify(uc)).join(", ");
    };

    // Helper function to parse use cases from string
    const parseUseCases = (useCasesStr) => {
        if (!useCasesStr.trim()) return [];
        try {
            return useCasesStr
                .split(",")
                .map((uc) => uc.trim())
                .filter(Boolean)
                .map((uc) => JSON.parse(uc));
        } catch (error) {
            console.error("Error parsing use cases:", error);
            return [];
        }
    };

    // Helper function to validate URL
    const isValidUrl = (urlString) => {
        if (!urlString) return false;
        try {
            const url = new URL(urlString);
            return url.protocol === "http:" || url.protocol === "https:";
        } catch (e) {
            return false;
        }
    };

    const [formData, setFormData] = useState({
        name: tool.name || "",
        description: tool.description || "",
        shortDescription: tool.shortDescription || "",
        externalUrl: tool.externalUrl || "",
        websiteUrl: tool.websiteUrl || "",
        categoryId: tool.categoryId || "",
        subCategoryId: tool.subCategoryId || "",
        pricingModel: tool.pricingModel || "",
        pricing: {
            hasFreeTier: tool.pricing?.hasFreeTier || false,
            hasTrial: tool.pricing?.hasTrial || false,
            plans: Array.isArray(tool.pricing?.plans) ? tool.pricing.plans : [],
            currency: tool.pricing?.currency || "USD",
        },
        pros: Array.isArray(tool.pros) ? tool.pros : [],
        cons: Array.isArray(tool.cons) ? tool.cons : [],
        video: tool.video || null,
        supportedPlatforms: Array.isArray(tool.supportedPlatforms)
            ? tool.supportedPlatforms
            : [],
        useCases: Array.isArray(tool.useCases) ? tool.useCases : [],
        useCasesDisplay: Array.isArray(tool.useCases)
            ? tool.useCases.map((uc) => JSON.stringify(uc)).join(", ")
            : "",
        features: Array.isArray(tool.features) ? tool.features : [],
        tags: Array.isArray(tool.tags) ? tool.tags : [],
        socialMediaUrls: tool.socialMediaUrls || {
            x: "",
            linkedin: "",
            facebook: "",
            instagram: "",
            youtube: "",
            pinterest: "",
            github: "",
            tiktok: "",
            reddit: "",
            discord: "",
        },
        logo_url: tool.logo_url || "",
        thumbnail: tool.thumbnail || "",
        dateCreated: tool.dateCreated || new Date().toISOString().split("T")[0],
    });

    useEffect(() => {
        const initialData = {
            name: tool.name || "",
            description: tool.description || "",
            shortDescription: tool.shortDescription || "",
            externalUrl: tool.externalUrl || "",
            websiteUrl: tool.websiteUrl || "",
            categoryId: tool.categoryId || "",
            subCategoryId: tool.subCategoryId || "",
            pricingModel: tool.pricingModel || "",
            pricing: {
                hasFreeTier: tool.pricing?.hasFreeTier || false,
                hasTrial: tool.pricing?.hasTrial || false,
                plans: Array.isArray(tool.pricing?.plans)
                    ? tool.pricing.plans
                    : [],
                currency: tool.pricing?.currency || "USD",
            },
            pros: Array.isArray(tool.pros) ? tool.pros : [],
            cons: Array.isArray(tool.cons) ? tool.cons : [],
            video: tool.video || null,
            supportedPlatforms: Array.isArray(tool.supportedPlatforms)
                ? tool.supportedPlatforms
                : [],
            useCases: Array.isArray(tool.useCases) ? tool.useCases : [],
            useCasesDisplay: Array.isArray(tool.useCases)
                ? tool.useCases.map((uc) => JSON.stringify(uc)).join(", ")
                : "",
            features: Array.isArray(tool.features) ? tool.features : [],
            tags: Array.isArray(tool.tags) ? tool.tags : [],
            socialMediaUrls: {
                ...(tool.socialMediaUrls || {
                    x: "",
                    linkedin: "",
                    facebook: "",
                    instagram: "",
                    youtube: "",
                    pinterest: "",
                    github: "",
                    tiktok: "",
                    reddit: "",
                    discord: "",
                }),
                // If external URL is a GitHub URL, set it as the GitHub URL
                github: tool.externalUrl?.includes("github.com")
                    ? tool.externalUrl
                    : tool.socialMediaUrls?.github || "",
            },
            logo_url: tool.logo_url || "",
            thumbnail: tool.thumbnail || "",
            dateCreated:
                tool.dateCreated || new Date().toISOString().split("T")[0],
        };
        setFormData(initialData);
    }, [tool]);

    const handleInputChange = (field, value) => {
        // If the field is externalUrl and it's a GitHub URL, also set the GitHub social media URL
        if (field === "externalUrl" && value.includes("github.com")) {
            setFormData((prev) => ({
                ...prev,
                [field]: value,
                socialMediaUrls: {
                    ...prev.socialMediaUrls,
                    github: value,
                },
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                [field]: value,
            }));
        }
    };

    const handleUseCasesChange = (value) => {
        try {
            // Split by comma and parse each use case
            const useCases = value
                .split(",")
                .map((uc) => uc.trim())
                .filter(Boolean)
                .map((uc) => {
                    try {
                        return JSON.parse(uc);
                    } catch (e) {
                        console.error("Error parsing use case:", e);
                        return null;
                    }
                })
                .filter(Boolean); // Remove any null values from failed parsing

            setFormData((prev) => ({
                ...prev,
                useCasesDisplay: value,
                useCases: useCases,
            }));
        } catch (error) {
            console.error("Error handling use cases:", error);
            // Keep the display value even if parsing fails
            setFormData((prev) => ({
                ...prev,
                useCasesDisplay: value,
            }));
        }
    };

    const handleFeaturesChange = (value) => {
        const features = value
            .split(",")
            .map((feature) => feature.trim())
            .filter(Boolean);
        setFormData((prev) => ({
            ...prev,
            features,
        }));
    };

    const handleTagsChange = (value) => {
        const tags = value.split(",").map((tag) => tag.trim());
        setFormData((prev) => ({
            ...prev,
            tags,
        }));
    };

    const handleArrayChange = (field, value) => {
        // Handle empty string case
        if (!value.trim()) {
            setFormData((prev) => ({
                ...prev,
                [field]: [],
            }));
            return;
        }
        const items = value
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean);
        setFormData((prev) => ({
            ...prev,
            [field]: items,
        }));
    };

    const handleSocialMediaChange = (platform, value) => {
        setFormData((prev) => ({
            ...prev,
            socialMediaUrls: {
                ...prev.socialMediaUrls,
                [platform]: value,
            },
        }));
    };

    const handlePricingChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            pricing: {
                ...prev.pricing,
                [field]: value,
            },
        }));
    };

    const handlePlansChange = (value) => {
        try {
            const plans = JSON.parse(value);
            setFormData((prev) => ({
                ...prev,
                pricing: {
                    ...prev.pricing,
                    plans: Array.isArray(plans) ? plans : [],
                },
            }));
        } catch (error) {
            console.error("Error parsing plans:", error);
        }
    };

    const handleProsConsChange = (field, value) => {
        const items = value
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean);
        setFormData((prev) => ({
            ...prev,
            [field]: items,
        }));
    };

    const handleVideoChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            video: {
                ...prev.video,
                [field]: value,
            },
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // Ensure arrays are properly formatted before submission
        const dataToSubmit = {
            ...formData,
            uuid: tool.uuid,
            pricing: {
                ...formData.pricing,
                hasFreeTier: Boolean(formData.pricing.hasFreeTier),
                hasTrial: Boolean(formData.pricing.hasTrial),
                plans: Array.isArray(formData.pricing.plans)
                    ? formData.pricing.plans
                    : [],
                currency: formData.pricing.currency || "USD",
            },
            supportedPlatforms: Array.isArray(formData.supportedPlatforms)
                ? formData.supportedPlatforms
                : [],
            pros: Array.isArray(formData.pros) ? formData.pros : [],
            cons: Array.isArray(formData.cons) ? formData.cons : [],
            tags: Array.isArray(formData.tags) ? formData.tags : [],
            useCases: Array.isArray(formData.useCases) ? formData.useCases : [],
            features: Array.isArray(formData.features) ? formData.features : [],
            dateCreated:
                formData.dateCreated || new Date().toISOString().split("T")[0],
        };
        // Remove the display field before saving
        const { useCasesDisplay, ...dataToSave } = dataToSubmit;
        onSave(dataToSave);
    };

    const handleImageError = (e, fallbackUrl) => {
        e.target.src = fallbackUrl || "https://via.placeholder.com/48";
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">
                    Basic Information
                </h3>

                {/* Image Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Logo URL */}
                    <div>
                        <label className="block font-medium text-gray-900">
                            Logo URL
                        </label>
                        <div className="mt-1">
                            <input
                                type="url"
                                value={formData.logo_url || ""}
                                onChange={(e) =>
                                    handleInputChange(
                                        "logo_url",
                                        e.target.value
                                    )
                                }
                                className="w-full p-2 border rounded-md"
                                placeholder="https://example.com/logo.png"
                            />
                        </div>
                        <div className="mt-2 flex items-center gap-4">
                            <div className="w-12 h-12 rounded-lg bg-gray-50 overflow-hidden flex items-center justify-center">
                                {formData.logo_url &&
                                isValidUrl(formData.logo_url) ? (
                                    <img
                                        src={formData.logo_url}
                                        alt="Logo preview"
                                        className="w-full h-full object-contain"
                                        onError={(e) => handleImageError(e)}
                                    />
                                ) : (
                                    <ImageIcon className="w-6 h-6 text-gray-400" />
                                )}
                            </div>
                            <p className="text-sm text-gray-500">
                                {formData.logo_url &&
                                !isValidUrl(formData.logo_url)
                                    ? "Invalid URL format"
                                    : "Preview"}
                            </p>
                        </div>
                    </div>

                    {/* Thumbnail URL */}
                    <div>
                        <label className="block font-medium text-gray-900">
                            Thumbnail URL
                        </label>
                        <div className="mt-1">
                            <input
                                type="url"
                                value={formData.thumbnail || ""}
                                onChange={(e) =>
                                    handleInputChange(
                                        "thumbnail",
                                        e.target.value
                                    )
                                }
                                className="w-full p-2 border rounded-md"
                                placeholder="https://example.com/thumbnail.png"
                            />
                        </div>
                        <div className="mt-2 flex items-center gap-4">
                            <div className="w-12 h-12 rounded-lg bg-gray-50 overflow-hidden flex items-center justify-center">
                                {formData.thumbnail &&
                                isValidUrl(formData.thumbnail) ? (
                                    <img
                                        src={formData.thumbnail}
                                        alt="Thumbnail preview"
                                        className="w-full h-full object-contain"
                                        onError={(e) => handleImageError(e)}
                                    />
                                ) : (
                                    <ImageIcon className="w-6 h-6 text-gray-400" />
                                )}
                            </div>
                            <p className="text-sm text-gray-500">
                                {formData.thumbnail &&
                                !isValidUrl(formData.thumbnail)
                                    ? "Invalid URL format"
                                    : "Preview"}
                            </p>
                        </div>
                    </div>
                </div>

                <div>
                    <label className="block font-medium text-gray-900">
                        Name
                    </label>
                    <input
                        type="text"
                        value={formData.name}
                        onChange={(e) =>
                            handleInputChange("name", e.target.value)
                        }
                        className="w-full p-2 border rounded-md"
                        required
                    />
                </div>

                <div>
                    <label className="block font-medium text-gray-900">
                        Short Description
                    </label>
                    <input
                        type="text"
                        value={formData.shortDescription}
                        onChange={(e) =>
                            handleInputChange(
                                "shortDescription",
                                e.target.value
                            )
                        }
                        className="w-full p-2 border rounded-md"
                        maxLength={500}
                    />
                </div>

                <div>
                    <label className="block font-medium text-gray-900">
                        Description
                    </label>
                    <textarea
                        value={formData.description}
                        onChange={(e) =>
                            handleInputChange("description", e.target.value)
                        }
                        className="w-full p-2 border rounded-md"
                        rows={4}
                        required
                    />
                </div>

                <div>
                    <label className="block font-medium text-gray-900">
                        Company
                    </label>
                    <input
                        type="text"
                        value={formData.company || ""}
                        onChange={(e) =>
                            handleInputChange("company", e.target.value)
                        }
                        className="w-full p-2 border rounded-md"
                    />
                </div>

                {/* Replace the category input with the dropdown component */}
                {categoryDropdown}
            </div>

            {/* URLs and Links */}
            <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">
                    URLs and Links
                </h3>
                <div>
                    <label className="block font-medium text-gray-900">
                        External URL
                    </label>
                    <input
                        type="url"
                        value={formData.externalUrl}
                        onChange={(e) =>
                            handleInputChange("externalUrl", e.target.value)
                        }
                        className="w-full p-2 border rounded-md"
                        required
                    />
                </div>

                <div>
                    <label className="block font-medium text-gray-900">
                        Website URL
                    </label>
                    <input
                        type="url"
                        value={formData.websiteUrl}
                        onChange={(e) =>
                            handleInputChange("websiteUrl", e.target.value)
                        }
                        className="w-full p-2 border rounded-md"
                        required
                    />
                </div>

                <div>
                    <label className="block font-medium text-gray-900">
                        Documentation URL
                    </label>
                    <input
                        type="url"
                        value={formData.documentationUrl || ""}
                        onChange={(e) =>
                            handleInputChange(
                                "documentationUrl",
                                e.target.value
                            )
                        }
                        className="w-full p-2 border rounded-md"
                    />
                </div>

                <div>
                    <label className="block font-medium text-gray-900">
                        Support URL
                    </label>
                    <input
                        type="url"
                        value={formData.supportUrl || ""}
                        onChange={(e) =>
                            handleInputChange("supportUrl", e.target.value)
                        }
                        className="w-full p-2 border rounded-md"
                    />
                </div>

                {/* Social Media URLs */}
                <div className="space-y-4">
                    <h4 className="text-md font-medium text-gray-900">
                        Social Media & Developer Platforms
                    </h4>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block font-medium text-gray-900">
                                X (Twitter) URL
                            </label>
                            <input
                                type="url"
                                value={formData.socialMediaUrls.x}
                                onChange={(e) =>
                                    handleSocialMediaChange("x", e.target.value)
                                }
                                className="w-full p-2 border rounded-md"
                                placeholder="https://x.com/..."
                            />
                        </div>

                        <div>
                            <label className="block font-medium text-gray-900">
                                LinkedIn URL
                            </label>
                            <input
                                type="url"
                                value={formData.socialMediaUrls.linkedin}
                                onChange={(e) =>
                                    handleSocialMediaChange(
                                        "linkedin",
                                        e.target.value
                                    )
                                }
                                className="w-full p-2 border rounded-md"
                                placeholder="https://linkedin.com/company/..."
                            />
                        </div>

                        <div>
                            <label className="block font-medium text-gray-900">
                                Facebook URL
                            </label>
                            <input
                                type="url"
                                value={formData.socialMediaUrls.facebook}
                                onChange={(e) =>
                                    handleSocialMediaChange(
                                        "facebook",
                                        e.target.value
                                    )
                                }
                                className="w-full p-2 border rounded-md"
                                placeholder="https://facebook.com/..."
                            />
                        </div>

                        <div>
                            <label className="block font-medium text-gray-900">
                                Instagram URL
                            </label>
                            <input
                                type="url"
                                value={formData.socialMediaUrls.instagram}
                                onChange={(e) =>
                                    handleSocialMediaChange(
                                        "instagram",
                                        e.target.value
                                    )
                                }
                                className="w-full p-2 border rounded-md"
                                placeholder="https://instagram.com/..."
                            />
                        </div>

                        <div>
                            <label className="block font-medium text-gray-900">
                                YouTube URL
                            </label>
                            <input
                                type="url"
                                value={formData.socialMediaUrls.youtube}
                                onChange={(e) =>
                                    handleSocialMediaChange(
                                        "youtube",
                                        e.target.value
                                    )
                                }
                                className="w-full p-2 border rounded-md"
                                placeholder="https://youtube.com/..."
                            />
                        </div>

                        <div>
                            <label className="block font-medium text-gray-900">
                                Pinterest URL
                            </label>
                            <input
                                type="url"
                                value={formData.socialMediaUrls.pinterest}
                                onChange={(e) =>
                                    handleSocialMediaChange(
                                        "pinterest",
                                        e.target.value
                                    )
                                }
                                className="w-full p-2 border rounded-md"
                                placeholder="https://pinterest.com/..."
                            />
                        </div>

                        <div>
                            <label className="block font-medium text-gray-900">
                                GitHub URL
                            </label>
                            <input
                                type="url"
                                value={formData.socialMediaUrls.github}
                                onChange={(e) =>
                                    handleSocialMediaChange(
                                        "github",
                                        e.target.value
                                    )
                                }
                                className="w-full p-2 border rounded-md"
                                placeholder="https://github.com/..."
                            />
                        </div>

                        <div>
                            <label className="block font-medium text-gray-900">
                                TikTok URL
                            </label>
                            <input
                                type="url"
                                value={formData.socialMediaUrls.tiktok}
                                onChange={(e) =>
                                    handleSocialMediaChange(
                                        "tiktok",
                                        e.target.value
                                    )
                                }
                                className="w-full p-2 border rounded-md"
                                placeholder="https://tiktok.com/@..."
                            />
                        </div>

                        <div>
                            <label className="block font-medium text-gray-900">
                                Reddit URL
                            </label>
                            <input
                                type="url"
                                value={formData.socialMediaUrls.reddit}
                                onChange={(e) =>
                                    handleSocialMediaChange(
                                        "reddit",
                                        e.target.value
                                    )
                                }
                                className="w-full p-2 border rounded-md"
                                placeholder="https://reddit.com/r/..."
                            />
                        </div>

                        <div>
                            <label className="block font-medium text-gray-900">
                                Discord URL
                            </label>
                            <input
                                type="url"
                                value={formData.socialMediaUrls.discord}
                                onChange={(e) =>
                                    handleSocialMediaChange(
                                        "discord",
                                        e.target.value
                                    )
                                }
                                className="w-full p-2 border rounded-md"
                                placeholder="https://discord.gg/..."
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Pricing Information */}
            <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">
                    Pricing Information
                </h3>

                <div>
                    <label className="block font-medium text-gray-900">
                        Pricing Model
                    </label>
                    <select
                        value={formData.pricingModel}
                        onChange={(e) =>
                            handleInputChange("pricingModel", e.target.value)
                        }
                        className="w-full p-2 border rounded-md"
                        required
                    >
                        <option value="">Select a pricing model</option>
                        <option value="Subscription">Subscription</option>
                        <option value="One-time">One-time</option>
                        <option value="Freemium">Freemium</option>
                        <option value="Free">Free</option>
                        <option value="Enterprise">Enterprise</option>
                        <option value="Custom">Custom</option>
                    </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={Boolean(formData.pricing.hasFreeTier)}
                                onChange={(e) =>
                                    handlePricingChange(
                                        "hasFreeTier",
                                        e.target.checked
                                    )
                                }
                                className="rounded"
                            />
                            <span className="font-medium text-gray-900">
                                Has Free Tier
                            </span>
                        </label>
                    </div>

                    <div>
                        <label className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={Boolean(formData.pricing.hasTrial)}
                                onChange={(e) =>
                                    handlePricingChange(
                                        "hasTrial",
                                        e.target.checked
                                    )
                                }
                                className="rounded"
                            />
                            <span className="font-medium text-gray-900">
                                Has Trial
                            </span>
                        </label>
                    </div>
                </div>

                <div>
                    <label className="block font-medium text-gray-900">
                        Currency
                    </label>
                    <input
                        type="text"
                        value={formData.pricing.currency}
                        onChange={(e) =>
                            handlePricingChange("currency", e.target.value)
                        }
                        className="w-full p-2 border rounded-md"
                        placeholder="USD"
                    />
                </div>

                <div>
                    <label className="block font-medium text-gray-900">
                        Pricing Plans
                    </label>
                    <textarea
                        value={JSON.stringify(formData.pricing.plans, null, 2)}
                        onChange={(e) => handlePlansChange(e.target.value)}
                        className="w-full p-2 border rounded-md font-mono text-sm min-h-[100px]"
                        placeholder={
                            '[{"name": "Basic", "price": 10, "features": ["Feature 1", "Feature 2"]}]'
                        }
                    />
                    <p className="text-sm text-gray-500 mt-1">
                        Format:{" "}
                        {
                            '[{"name": "Basic", "price": 10, "features": ["Feature 1", "Feature 2"]}]'
                        }
                    </p>
                </div>
            </div>

            {/* Pros and Cons */}
            <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">
                    Pros and Cons
                </h3>

                <div>
                    <label className="block font-medium text-gray-900">
                        Pros
                    </label>
                    <textarea
                        value={formData.pros.join(", ")}
                        onChange={(e) =>
                            handleProsConsChange("pros", e.target.value)
                        }
                        className="w-full p-2 border rounded-md"
                        placeholder="Enter pros separated by commas"
                        rows={3}
                    />
                </div>

                <div>
                    <label className="block font-medium text-gray-900">
                        Cons
                    </label>
                    <textarea
                        value={formData.cons.join(", ")}
                        onChange={(e) =>
                            handleProsConsChange("cons", e.target.value)
                        }
                        className="w-full p-2 border rounded-md"
                        placeholder="Enter cons separated by commas"
                        rows={3}
                    />
                </div>
            </div>

            {/* Video Information */}
            <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">
                    Video Information
                </h3>

                <div>
                    <label className="block font-medium text-gray-900">
                        Video URL
                    </label>
                    <input
                        type="url"
                        value={formData.video?.url || ""}
                        onChange={(e) =>
                            handleVideoChange("url", e.target.value)
                        }
                        className="w-full p-2 border rounded-md"
                        placeholder="https://youtube.com/..."
                    />
                </div>

                <div>
                    <label className="block font-medium text-gray-900">
                        Video Thumbnail
                    </label>
                    <input
                        type="url"
                        value={formData.video?.thumbnail || ""}
                        onChange={(e) =>
                            handleVideoChange("thumbnail", e.target.value)
                        }
                        className="w-full p-2 border rounded-md"
                        placeholder="https://example.com/thumbnail.jpg"
                    />
                </div>

                <div>
                    <label className="block font-medium text-gray-900">
                        Video Duration
                    </label>
                    <input
                        type="text"
                        value={formData.video?.duration || ""}
                        onChange={(e) =>
                            handleVideoChange("duration", e.target.value)
                        }
                        className="w-full p-2 border rounded-md"
                        placeholder="e.g., 5:30"
                    />
                </div>
            </div>

            {/* Additional Information */}
            <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">
                    Additional Information
                </h3>
                <div>
                    <label className="block font-medium text-gray-900">
                        Tags
                    </label>
                    <input
                        type="text"
                        value={formData.tags?.join(", ")}
                        onChange={(e) => handleTagsChange(e.target.value)}
                        className="w-full p-2 border rounded-md"
                        placeholder="Enter tags separated by commas"
                    />
                </div>

                <div>
                    <label className="block font-medium text-gray-900">
                        Features
                    </label>
                    <input
                        type="text"
                        value={
                            Array.isArray(formData.features)
                                ? formData.features.join(", ")
                                : ""
                        }
                        onChange={(e) => handleFeaturesChange(e.target.value)}
                        className="w-full p-2 border rounded-md"
                        placeholder="Enter features separated by commas"
                    />
                </div>

                <div>
                    <label className="block font-medium text-gray-900">
                        Supported Platforms
                    </label>
                    <input
                        type="text"
                        value={
                            Array.isArray(formData.supportedPlatforms)
                                ? formData.supportedPlatforms.join(", ")
                                : ""
                        }
                        onChange={(e) =>
                            handleArrayChange(
                                "supportedPlatforms",
                                e.target.value
                            )
                        }
                        className="w-full p-2 border rounded-md"
                        placeholder="Enter platforms separated by commas"
                    />
                </div>

                <div>
                    <label className="block font-medium text-gray-900">
                        Use Cases
                    </label>
                    <textarea
                        value={formData.useCasesDisplay}
                        onChange={(e) => handleUseCasesChange(e.target.value)}
                        className="w-full p-2 border rounded-md min-h-[100px] font-mono text-sm"
                        placeholder='{"title": "Use Case 1", "description": "Description 1"}, {"title": "Use Case 2", "description": "Description 2"}'
                    />
                    <p className="text-sm text-gray-500 mt-1">
                        Format: {"{"}"title": "Use Case 1", "description":
                        "Description 1"{"}"}, {"{"}"title": "Use Case 2",
                        "description": "Description 2"{"}"}
                    </p>
                </div>

                <div>
                    <label className="block font-medium text-gray-900">
                        Date Created
                    </label>
                    <input
                        type="date"
                        value={formData.dateCreated}
                        onChange={(e) =>
                            handleInputChange("dateCreated", e.target.value)
                        }
                        className="w-full p-2 border rounded-md"
                        required
                    />
                </div>

                <div>
                    <label className="block font-medium text-gray-900">
                        Twitter Handle
                    </label>
                    <input
                        type="text"
                        value={formData.twitterHandle || ""}
                        onChange={(e) =>
                            handleInputChange("twitterHandle", e.target.value)
                        }
                        className="w-full p-2 border rounded-md"
                        placeholder="@username"
                    />
                </div>
            </div>

            {/* Form Actions */}
            <div className="flex gap-4 mt-6">
                <button
                    type="submit"
                    className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center justify-center gap-2"
                >
                    <Save className="h-4 w-4" />
                    Save Changes
                </button>
                <button
                    type="button"
                    onClick={onCancel}
                    className="flex-1 bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 flex items-center justify-center gap-2"
                >
                    <X className="h-4 w-4" />
                    Cancel
                </button>
            </div>
        </form>
    );
};

export default ToolEditForm;
